import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  fetchCorporateCampaign,
  sendCorporateCampaignEmails,
  sendSingleCorporateEmail,
  downloadCorporatePdf,
  previewAppendCorporateDocuments,
  appendCorporateDocuments,
  deleteCorporateDocument,
} from '../../api/index'
import importStyles from '../ImportMensuel/ImportMensuel.module.css'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import styles from '../ImportMensuel/CampagneDetail.module.css'
import { formatCampaignDisplayLabel, describeCampaignQuinzaine, getEmailPreviewForToday } from '../../utils/corporateQuinzaine'

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'sent', label: 'Envoyées' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échecs' },
]

const statusLabel = {
  sent: { text: 'Envoyée', cls: 'sent' },
  failed: { text: 'Échec', cls: 'failed' },
  pending: { text: 'En attente', cls: 'pending' },
}

const PAGE_SIZE = 10
const isPdf = (f) => f?.name?.toLowerCase().endsWith('.pdf')

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const CorporateCampagneDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [sending, setSending] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [confirmModal, setConfirmModal] = useState(null)
  const [appendOpen, setAppendOpen] = useState(false)
  const [addFiles, setAddFiles] = useState([])
  const [addPreview, setAddPreview] = useState(null)
  const [addLoading, setAddLoading] = useState(false)
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)
  const addFilesKeyRef = useRef('')

  const fetchCampaign = useCallback(async () => {
    try {
      const { data: result } = await fetchCorporateCampaign(id)
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchCampaign()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!data || sending) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('sendAll') === '1') {
      window.history.replaceState({}, '', `/corporate/campagnes/${id}`)
      requestSendEmails(null)
    }
    // eslint-disable-next-line
  }, [data])

  useEffect(() => {
    setPage(1)
  }, [search, statusFilter])

  const { campaign, documents = [] } = data || {}

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return documents.filter((d) => {
      const status = d.emailStatus || 'pending'
      if (statusFilter !== 'all' && status !== statusFilter) return false
      if (!q) return true
      return [d.clientName, d.fileName, d.client?.email, d.client?.matriculeFiscal]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
  }, [documents, search, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const stats = useMemo(() => {
    const sent = documents.filter((d) => d.emailStatus === 'sent').length
    const failed = documents.filter((d) => d.emailStatus === 'failed').length
    const pending = documents.filter((d) => !d.emailStatus || d.emailStatus === 'pending').length
    const total = sent + pending + failed
    return { sent, failed, pending, progress: total ? Math.round((sent / total) * 100) : 0 }
  }, [documents])

  const readyToSend = useMemo(
    () => documents.filter((d) => d.client?.email && d.emailStatus !== 'sent').length,
    [documents]
  )

  const selectedSendable = useMemo(
    () => selected.filter((docId) => {
      const doc = documents.find((d) => d._id === docId)
      return doc?.client?.email
    }).length,
    [selected, documents]
  )

  const downloadPdf = async (documentId, fileName) => {
    try {
      const response = await downloadCorporatePdf(id, documentId)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    }
  }

  const executeSendEmails = async (documentIds = null) => {
    setSending(true)
    try {
      const { data: result } = await sendCorporateCampaignEmails(id, documentIds ? { documentIds } : {})
      openSnackbar(result.message)
      fetchCampaign()
      setSelected([])
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi')
    } finally {
      setSending(false)
    }
  }

  const requestSendEmails = (documentIds = null) => {
    const count = documentIds ? documentIds.length : documents.length
    if (!count) {
      openSnackbar('Aucun document à envoyer')
      return
    }
    setConfirmModal({
      documentIds,
      variant: 'info',
      title: 'Envoyer les emails',
      message: documentIds
        ? `Envoyer ${count} email(s) sélectionné(s) ?`
        : `Envoyer tous les emails (${count}) ?`,
      detail: 'Les PDF seront joints aux emails des clients convention.',
      confirmLabel: 'Envoyer',
    })
  }

  const sendOne = async (documentId) => {
    setSending(true)
    try {
      const { data: result } = await sendSingleCorporateEmail(id, documentId)
      openSnackbar(result.message)
      fetchCampaign()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi')
    } finally {
      setSending(false)
    }
  }

  const resetAppendForm = () => {
    setAddFiles([])
    setAddPreview(null)
    addFilesKeyRef.current = ''
  }

  const applyAddFiles = (incoming) => {
    const list = Array.from(incoming || []).filter(isPdf)
    if (!list.length) {
      openSnackbar('Sélectionnez des fichiers PDF')
      return
    }
    setAddFiles(list)
    setAddPreview(null)
    addFilesKeyRef.current = ''
  }

  const runAppendPreview = useCallback(async (files) => {
    if (!files?.length) return null
    const key = files.map((f) => `${f.name}-${f.size}`).join('|')
    if (key === addFilesKeyRef.current && addPreview) return addPreview

    setAddLoading(true)
    try {
      const formData = new FormData()
      files.forEach((f) => formData.append('files', f))
      const { data: result } = await previewAppendCorporateDocuments(id, formData)
      setAddPreview(result)
      addFilesKeyRef.current = key
      return result
    } catch (error) {
      setAddPreview(null)
      addFilesKeyRef.current = ''
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur analyse PDF')
      return null
    } finally {
      setAddLoading(false)
    }
  }, [id, addPreview])

  useEffect(() => {
    if (!appendOpen || !addFiles.length) {
      if (!addFiles.length) setAddPreview(null)
      return undefined
    }
    runAppendPreview(addFiles)
  }, [appendOpen, addFiles, runAppendPreview])

  const executeAppend = async () => {
    if (!addFiles.length || !addPreview?.validCount) return
    setAddSubmitting(true)
    try {
      const formData = new FormData()
      addFiles.forEach((f) => formData.append('files', f))
      const { data: result } = await appendCorporateDocuments(id, formData)
      openSnackbar(result.message)
      resetAppendForm()
      setAppendOpen(false)
      await fetchCampaign()
      if (result.addedSuccess > 0) {
        const sendableIds = (result.addedDocumentIds || []).filter(Boolean)
        if (sendableIds.length) {
          setConfirmModal({
            type: 'sendAfterAppend',
            variant: 'info',
            title: 'PDF ajoutés',
            message: `${result.addedSuccess} facture(s) ajoutée(s) à cette campagne.`,
            detail: 'Souhaitez-vous envoyer les emails aux clients nouvellement ajoutés ?',
            confirmLabel: 'Envoyer les emails',
            cancelLabel: 'Plus tard',
            documentIds: sendableIds,
          })
        }
      }
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur ajout PDF')
    } finally {
      setAddSubmitting(false)
    }
  }

  const requestAppend = () => {
    if (!addFiles.length) {
      openSnackbar('Sélectionnez des PDF')
      return
    }
    if (addLoading) {
      openSnackbar('Analyse en cours…')
      return
    }
    if (!addPreview?.validCount) {
      openSnackbar('Aucun PDF valide à ajouter')
      return
    }
    if (addPreview?.hasDuplicates) {
      openSnackbar('Corrigez les doublons avant d\'ajouter')
      return
    }
    setConfirmModal({
      type: 'append',
      variant: 'info',
      title: 'Ajouter à la campagne',
      message: `Ajouter ${addPreview.validCount} PDF à « ${addPreview.campaignLabel || formatCampaignDisplayLabel(campaign)} » ?`,
      detail: 'Les factures seront comptabilisées dans la même quinzaine. Les clients déjà présents seront refusés.',
      confirmLabel: 'Ajouter',
    })
  }

  const requestDeleteDocument = (doc) => {
    setConfirmModal({
      type: 'deleteDocument',
      variant: doc.emailStatus === 'sent' ? 'warning' : 'info',
      title: 'Retirer de la campagne',
      message: `Retirer « ${doc.clientName} » de cette campagne ?`,
      detail: doc.emailStatus === 'sent'
        ? 'L\'email a déjà été envoyé. Le PDF sera supprimé de la campagne mais le client reste en base.'
        : 'Le PDF sera retiré de cette quinzaine. Le client reste en base et pourra être réimporté plus tard.',
      confirmLabel: 'Retirer',
      documentIds: [doc._id],
      clientName: doc.clientName,
    })
  }

  const executeDeleteDocument = async (documentId) => {
    setDeleting(true)
    try {
      const { data: result } = await deleteCorporateDocument(id, documentId)
      openSnackbar(result.message)
      setSelected((prev) => prev.filter((x) => x !== documentId))
      await fetchCampaign()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
    }
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    const { type, documentIds } = confirmModal
    setConfirmModal(null)
    if (type === 'append') {
      executeAppend()
      return
    }
    if (type === 'sendAfterAppend') {
      if (documentIds?.length) executeSendEmails(documentIds)
      return
    }
    if (type === 'deleteDocument') {
      executeDeleteDocument(documentIds?.[0])
      return
    }
    executeSendEmails(documentIds ?? null)
  }

  if (loading) {
    return <div className={styles.loading}><Spinner /></div>
  }

  if (!campaign) return null

  const periodLabel = formatCampaignDisplayLabel(campaign)
  const quinzaineDescription = describeCampaignQuinzaine(
    campaign.month,
    campaign.year,
    campaign.quinzaine || 1,
  )
  const emailPreviewToday = getEmailPreviewForToday()

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => history.push('/corporate/dashboard')}>
        ← Dashboard Corporate
      </button>

      <div className={styles.topBar}>
        <div>
          <h1>{campaign.label || `Campagne ${periodLabel}`}</h1>
          <p>{campaign.successCount} PDF · Corporate conventions</p>
        </div>
      </div>

      <div className={styles.periodBanner}>
        <div>
          <strong>Période : {periodLabel}</strong>
          <p style={{ margin: '6px 0 0', fontSize: '0.88rem', color: '#475569', fontWeight: 400 }}>
            {quinzaineDescription}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: '#64748b', fontWeight: 400 }}>
            {emailPreviewToday}
          </p>
        </div>
        <span>{stats.progress}% emails envoyés</span>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>PDF</span>
          <span className={styles.kpiValue}>{documents.length}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Envoyés</span>
          <span className={styles.kpiValue}>{stats.sent}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>En attente</span>
          <span className={styles.kpiValue}>{stats.pending}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Échecs</span>
          <span className={styles.kpiValue}>{stats.failed}</span>
        </div>
      </div>

      <div className={styles.appendPanel}>
        <div className={styles.appendPanelHeader}>
          <div>
            <h3>Compléter la campagne</h3>
            <p>
              Clients oubliés ? Ajoutez leurs PDF ici — ils seront rattachés à la même quinzaine
              ({periodLabel}).
            </p>
          </div>
          <button
            type="button"
            className={styles.appendToggleBtn}
            onClick={() => {
              setAppendOpen((open) => {
                if (open) resetAppendForm()
                return !open
              })
            }}
          >
            {appendOpen ? 'Fermer' : '+ Ajouter des PDF'}
          </button>
        </div>

        {appendOpen && (
          <div className={styles.appendBody}>
            <div className={styles.appendNote}>
              Le nom du fichier PDF doit correspondre <strong>exactement</strong> au nom du client en base.
              Un client déjà présent dans cette campagne sera refusé automatiquement.
            </div>

            <div
              className={importStyles.uploadZone}
              onDragEnter={(e) => { e.preventDefault(); dragCounter.current += 1; setIsDragging(true) }}
              onDragLeave={(e) => { e.preventDefault(); dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false) } }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); dragCounter.current = 0; setIsDragging(false); applyAddFiles(e.dataTransfer.files) }}
            >
              <label
                htmlFor="corp-append-pdfs"
                className={`${importStyles.uploadLabel} ${isDragging ? importStyles.uploadDragActive : ''} ${addFiles.length ? importStyles.uploadHasFile : ''}`}
              >
                <span className={importStyles.uploadIcon}>{isDragging ? '📥' : '📄'}</span>
                <strong>
                  {isDragging
                    ? 'Déposez les PDF ici'
                    : addFiles.length
                      ? `${addFiles.length} PDF sélectionné(s)`
                      : 'Sélectionner les PDF oubliés'}
                </strong>
                <span className={importStyles.uploadHint}>Analyse automatique · même règles que l&apos;import initial</span>
                <span className={importStyles.uploadBtn}>Parcourir</span>
              </label>
              <input
                id="corp-append-pdfs"
                type="file"
                accept=".pdf"
                multiple
                className={importStyles.fileInput}
                onChange={(e) => applyAddFiles(e.target.files)}
              />
            </div>

            {addLoading && addFiles.length > 0 && (
              <p className={styles.appendLoading}>Analyse des PDF en cours…</p>
            )}

            {addPreview && (
              <div className={styles.appendPreviewWrap}>
                <div className={styles.appendPreviewHead}>
                  <span>Résultat de l&apos;analyse</span>
                  <div className={styles.appendPreviewStats}>
                    <span><strong>{addPreview.validCount}</strong> valide(s)</span>
                    <span><strong>{addPreview.errorCount}</strong> erreur(s)</span>
                    {addPreview.newClients > 0 && (
                      <span><strong>{addPreview.newClients}</strong> nouveau(x) client(s)</span>
                    )}
                  </div>
                </div>
                <div className={`${styles.tableWrap} table-scroll-wrap`}>
                <table className={`${styles.appendPreviewTable} responsive-table`}>
                  <thead>
                    <tr>
                      <th>Fichier</th>
                      <th>Client</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addPreview.rows.map((row) => {
                      const inCampaign = row.rowErrors.some((e) => e.includes('campagne'))
                      const rowClass = row.valid
                        ? styles.appendRowOk
                        : inCampaign
                          ? styles.appendRowErr
                          : styles.appendRowWarn
                      const status = row.valid
                        ? 'Prêt'
                        : row.rowErrors.join(' · ')
                      return (
                        <tr key={`${row.fileName}-${row.rowIndex}`}>
                          <td data-label="Fichier">{row.fileName}</td>
                          <td data-label="Client">{row.clientName || '—'}</td>
                          <td data-label="Statut" className={rowClass}>{status}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            <div className={styles.appendActions}>
              <button
                type="button"
                className={styles.btnAppendSecondary}
                onClick={resetAppendForm}
                disabled={addSubmitting || !addFiles.length}
              >
                Effacer
              </button>
              <button
                type="button"
                className={styles.btnAppendPrimary}
                onClick={requestAppend}
                disabled={addSubmitting || addLoading || !addPreview?.validCount || addPreview?.hasDuplicates}
              >
                {addSubmitting
                  ? 'Ajout en cours…'
                  : `Ajouter à la campagne (${addPreview?.validCount || 0})`}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.sendPanel}>
        <div className={styles.sendPanelInfo}>
          <h3>Envoi des emails clients</h3>
          <p>
            {stats.pending} en attente · {stats.sent} envoyés
            {stats.failed > 0 ? ` · ${stats.failed} échec(s)` : ''}
          </p>
        </div>
        <div className={styles.sendActions}>
          <button
            type="button"
            className={styles.btnSendSelect}
            disabled={sending || selectedSendable === 0}
            onClick={() => requestSendEmails(selected)}
          >
            <IconMail />
            Sélection
            {selectedSendable > 0 && <span className={styles.sendCount}>{selectedSendable}</span>}
          </button>
          <button
            type="button"
            className={styles.btnSendAll}
            disabled={sending || readyToSend === 0}
            onClick={() => requestSendEmails(null)}
          >
            <IconMail />
            {sending ? 'Envoi en cours…' : 'Envoyer tout'}
            {!sending && readyToSend > 0 && <span className={styles.sendBadge}>{readyToSend}</span>}
          </button>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Documents PDF</h2>
          <span className={styles.pageInfo}>
            {filtered.length} résultat(s)
            {(search || statusFilter !== 'all') && ` sur ${documents.length}`}
          </span>
        </div>

        <div className={styles.toolbar}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Rechercher client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Rechercher un client"
          />
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrer par statut d'envoi"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {(search || statusFilter !== 'all') && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => { setSearch(''); setStatusFilter('all') }}
            >
              Réinitialiser
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <p className={styles.empty}>Aucun document ne correspond à votre recherche.</p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && filtered.every((d) => selected.includes(d._id))}
                        onChange={() => {
                          const ids = filtered.map((d) => d._id)
                          const all = ids.every((i) => selected.includes(i))
                          setSelected(all ? selected.filter((x) => !ids.includes(x)) : [...new Set([...selected, ...ids])])
                        }}
                        aria-label="Tout sélectionner (filtre actif)"
                      />
                    </th>
                    <th>Client</th>
                    <th>Fichier</th>
                    <th>Email</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((doc) => {
                    const st = statusLabel[doc.emailStatus || 'pending']
                    return (
                      <tr key={doc._id}>
                        <td data-label="Sélection">
                          <input
                            type="checkbox"
                            checked={selected.includes(doc._id)}
                            onChange={() => setSelected((prev) => prev.includes(doc._id) ? prev.filter((x) => x !== doc._id) : [...prev, doc._id])}
                          />
                        </td>
                        <td data-label="Client">{doc.clientName}</td>
                        <td data-label="Fichier">{doc.fileName}</td>
                        <td data-label="Email">{doc.client?.email || '—'}</td>
                        <td data-label="Statut"><span className={`${styles.badge} ${styles[st.cls]}`}>{st.text}</span></td>
                        <td className={styles.actionCell} data-label="Actions">
                          <button type="button" className={styles.pdfBtn} onClick={() => downloadPdf(doc._id, doc.fileName)}>PDF</button>
                          <button type="button" className={styles.emailBtn} disabled={sending || !doc.client?.email} onClick={() => sendOne(doc._id)}>
                            {doc.emailStatus === 'sent' ? 'Renvoyer' : 'Envoyer'}
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                            onClick={() => requestDeleteDocument(doc)}
                            disabled={deleting || sending}
                            title="Retirer de la campagne"
                            aria-label={`Retirer ${doc.clientName} de la campagne`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>Page {safePage} / {totalPages}</span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        open={!!confirmModal}
        variant={confirmModal?.variant || 'info'}
        title={confirmModal?.title}
        message={confirmModal?.message}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Confirmer'}
        cancelLabel={confirmModal?.cancelLabel || 'Annuler'}
        onConfirm={handleConfirmModal}
        onCancel={() => setConfirmModal(null)}
        loading={sending || addSubmitting || deleting}
      />
    </div>
  )
}

export default CorporateCampagneDetail
